<template lang="pug">
.home(v-if="tableData")
  j-master-menu-component(
    :search-info="customSearchInfo ? searchInfo.search : searchInfo"
    :search-text="$t(searchTextKey)"
    @inputting="($event) => handleSearchInput($event, customSearchInfo ? 'search' : null)"
    @open-add-dialog="onClickOpenAddDialog"
    @open-sort-dialog ="onClickOpenSortDialog"
    @handle-change-active="handleChangeActive"
    @export-excel="onClickExportExcel"
    :isSortable="isSortable"
    :isActive="isActive"
    :enableSearch="enableSearch"
    :readOnly="readOnly"
    :isMaster="isMaster"
    :enableExcelExport="enableExcelExport"
    :excelExportLoading="exporting"
  )
    template(v-for='(_, name) in $scopedSlots' #[name]='slotProps')
      slot(v-bind='slotProps', :name='name')
  j-master-item-list(
    :headers="headers"
    :items="tableData.map((item) => ({...item }))"
    :actions="actions"
    @on-delete="openConfirmDelete"
    @on-update="onClickOpenUpdateDialog"
    @on-duplicate="onClickOpenDuplicateDialog"
    @handle-revert-item="handleRevertItem"
    :loading="loading"
    :isActive="isActive"
    :maxWidth="customMaxWidth"
    :on-no-data-message="noDataMessage"
    :itemKey="itemKey"
    :readOnly="readOnly"
    :isMaster="isMaster"
  )
    template(#footer)
      pagination(
        :isDisabled="!isOpenSortDialog"
        :currentPage="currentPage"
        :totalPage="totalPage"
        :totalItem="totalItem"
        :pageSize="pageSize"
        :items="items"
        @onChange="onChangePage"
      )
    template(v-for='(_, name) in $scopedSlots' #[name]='slotProps')
      slot(v-bind='slotProps', :name='name')
  j-confirm-delete(
    :show-dialog="isShowConfirmDelete"
    :item-label="curData.name_short ? curData.name_short : curData[`${itemLabel}`]"
    @on-close="onDelete"
    :is-delete-master = "true"
    :cancel-text="$t('common.no')"
    :ok-text="$t('common.yes')"
  )
  j-sort-dialog(
    :value="isOpenSortDialog"
    :endpoint="endpoint"
    :query-params="generateSearchParams ? generateSearchParams(searchInfo.value) : {}"
    @on-close="isOpenSortDialog = false"
    @update-order-num="updateOrderNum"
  )
  j-confirm-dialog(
    :showDialog="showConfirmUsedDialog"
    :messages="[$t('message.confirm_used_in_itm_var')]"
    :cancel-text="$t('common.no')"
    :del-text="$t('common.yes')"
    @on-cancel="showConfirmUsedDialog=false"
    @on-close="showConfirmUsedDialog=false"
    @on-ok="onDelete('delete')"
  )
  j-confirm-dialog(
    :showDialog="showConfirmRevertDialog"
    :messages="[$t('message.confirm_data_revert')]"
    :cancel-text="$t('common.no')"
    :del-text="$t('common.yes')"
    @on-cancel="showConfirmRevertDialog=false"
    @on-close="showConfirmRevertDialog=false"
    @on-ok="onRevert()"
  )
  slot(name = "dialogEdit" :readOnly="readOnly" :isMaster="isMaster")
</template>
<script>
import {
  defineComponent,
  getCurrentInstance,
  ref,
  onMounted,
  computed
} from 'vue'
import {
  JMasterItemList,
  JMasterMenuComponent,
  JConfirmDelete,
  JSortDialog,
  JConfirmDialog
} from '@/components'
import { api } from '@/plugins'
import { master, showError, replaceUrl, downloadFile, buildQuery } from '@/utils'
import router from '@/router'
import { debounce } from 'lodash'
import dayjs from 'dayjs'
import Pagination from '@/components/Pagination'
import { defaultHeader } from './index'
import { usePagination } from '@/composables/useMaster'
import { validateReadOnlyLoginUser } from '@/utils/auth'

const JMasterPage = defineComponent({
  components: {
    JMasterMenuComponent,
    JMasterItemList,
    JConfirmDelete,
    JSortDialog,
    JConfirmDialog,
    Pagination
  },
  props: {
    // table headers for f-master-item-list table
    // if not includes use default header
    tableHeaders: {
      type: Array,
      required: false
    },
    // for placeholder of search field
    searchTextKey: {
      type: String,
      required: true
    },
    // api endpoint
    endpoint: {
      type: String,
      required: true
    },
    // function get additional data on mount component
    additionOnMountFunc: {
      type: Function,
      required: false
    },
    customSearchInfo: {
      type: [String, Object, undefined],
      required: false
    },
    generateSearchParams: {
      type: [Function, undefined],
      required: false
    },
    customMaxWidth: {
      type: Number,
      required: false
    },
    itemKey: {
      type: String,
      required: false
    },
    enableSearch: {
      type: Boolean,
      default: true,
      required: false
    },
    itemLabel: {
      type: String,
      default: 'name'
    },
    isSortable: {
      type: Boolean,
      default: true
    },
    noDataMessage: {
      type: String || null,
      default: null,
      required: false
    },
    jobCode: {
      type: String,
      default: null,
      required: false
    },
    defaultActions: {
      type: Array,
      default: null,
      required: false
    },
    enableExcelExport: {
      type: Boolean,
      default: false
    },
    exportFileNamePrefix: {
      type: String,
      required: false
    }
  },
  setup(props, { emit }) {
    const instance = getCurrentInstance()
    const { $toast, $root } = instance.proxy
    const headers = ref(defaultHeader)
    const readOnly = ref(false)
    const isMaster = ref(true)
    const query = router.currentRoute.query
    const searchInfo = ref(
      props.customSearchInfo ? props.customSearchInfo : query.search || ''
    )
    const isShowConfirmDelete = ref(false)
    const curData = ref({})
    const isShowDialogUpdate = ref(false)
    const isAdd = ref(false) // add or edit
    const isOpenSortDialog = ref(false)
    const isActive = ref(query.act ? query.act === 'true' : true)
    const tableData = ref([])
    const showConfirmUsedDialog = ref(false)
    const exporting = ref(false)

    const actions = ref(props.defaultActions || master.actionDotsVertical)

    const {
      currentPage,
      pageSize,
      totalPage,
      totalItem,
      items,
      loading,
      getItems
    } = usePagination(props.endpoint)

    const getData = async (query) => {
      try {
        const isVarietiesApi = props.endpoint === '/varieties'
        await getItems({
          searchstr:
            typeof searchInfo.value == 'string' ? searchInfo.value : null,
          active: isActive.value,
          ...(isVarietiesApi ? { ignore_item_active_filter: true } : {}),
          ...query
        })
        replaceUrl({
          query: { ...router.currentRoute.query, act: isActive.value }
        })
        tableData.value = items.value
      } catch (e) {
        showError(e, $toast, $root.$t('message.get_data_failed'))
      }
    }

    const handleSearchInput = debounce((value, field) => {
      field ? (searchInfo.value[field] = value) : (searchInfo.value = value)
      currentPage.value = 1
      getData(
        props.generateSearchParams
          ? props.generateSearchParams(searchInfo.value)
          : undefined
      )
      replaceUrl({
        query: {
          ...router.currentRoute.query,
          [field ? field : 'search']: value
        }
      }) //change in url
    }, 500)

    const onClickOpenUpdateDialog = (item) => {
      isAdd.value = false
      curData.value = item
      isShowDialogUpdate.value = true
      // emit and set values for dialog edit or create
      emit('openUpdateDialog', {
        isAdd: isAdd.value,
        curData: curData.value,
        isShowDialog: isShowDialogUpdate.value
      })
    }

    const onClickOpenDuplicateDialog = (item) => {
      isAdd.value = true
      curData.value = item
      isShowDialogUpdate.value = true
      emit('openUpdateDialog', {
        isAdd: isAdd.value,
        curData: curData.value,
        isShowDialog: isShowDialogUpdate.value
      })
    }

    const openConfirmDelete = async (item) => {
      curData.value = item
      isShowConfirmDelete.value = true
    }

    const onClickOpenAddDialog = () => {
      isAdd.value = true
      isShowDialogUpdate.value = true
      // emit and set values for dialog edit or create
      emit('openUpdateDialog', {
        isAdd: isAdd.value,
        isShowDialog: isShowDialogUpdate.value
      })
    }
    const onClickOpenSortDialog = () => {
      isOpenSortDialog.value = true
    }
    const deleteAPI = async () => {
      try {
        await api.delete(`${props.endpoint}/${curData.value.id}`, {
          data: {
            active: false
          }
        })
        $toast.success($root.$t('message.delete_successful'))
        reload()
      } catch (e) {
        showError(e, $toast, $root.$t('message.delete_failed'))
      }
    }

    const onDelete = (mode) => {
      isShowConfirmDelete.value = false
      showConfirmUsedDialog.value = false
      if (mode === 'delete') {
        deleteAPI()
      }
    }

    const reload = () => {
      window.scrollTo({
        top: 0,
        behavior: 'instant'
      })
      getData(
        props.generateSearchParams
          ? props.generateSearchParams(searchInfo.value)
          : undefined
      )
    }

    const updateOrderNum = async (itemList) => {
      try {
        await api.patch(`${props.endpoint}/sort`, itemList)
        $toast.success($root.$t('message.update_successful'))
        isOpenSortDialog.value = false
        reload()
      } catch (e) {
        showError(e, $toast, $root.$t('master.msg.update_failed'))
      }
    }

    const parseBlobErrorReasonCode = async (e) => {
      if (!(e.response?.data instanceof Blob)) return null
      try {
        const body = JSON.parse(await e.response.data.text())
        return body.reason_code || null
      } catch (err) {
        return null
      }
    }

    const exportExcel = async () => {
      exporting.value = true
      try {
        const { data } = await api.get(
          `${props.endpoint}/export_excel?${buildQuery({ active: isActive.value })}`,
          { responseType: 'blob' }
        )
        const fileName = `${props.exportFileNamePrefix}_${dayjs().format(
          'YYYYMMDDHHmmss'
        )}`
        downloadFile(data, 'xlsx', fileName)
      } catch (e) {
        const reasonCode = await parseBlobErrorReasonCode(e)
        if (reasonCode === 'export_data_is_not_found') {
          $toast.error($root.$t('message.export_no_data'))
        } else {
          showError(e, $toast, $root.$t('message.export_excel_failed'))
        }
      } finally {
        exporting.value = false
      }
    }

    const onClickExportExcel = () => {
      instance.proxy.$confirm({
        messages: [$root.$t('message.confirm_export_excel')],
        onConfirm: exportExcel
      })
    }

    const showConfirmRevertDialog = ref(false)
    const revertItem = ref({})
    const handleRevertItem = (item) => {
      showConfirmRevertDialog.value = true
      revertItem.value = item
    }
    const onRevert = async () => {
      revertItem.value.active = true
      try {
        await api.put(
          `${props.endpoint}/${revertItem.value.id}`,
          revertItem.value
        )
        $toast.success($root.$t('message.revert_successful'))
        await getData(
          props.generateSearchParams
            ? props.generateSearchParams(searchInfo.value)
            : undefined
        )
      } finally {
        showConfirmRevertDialog.value = false
      }
    }

    const tempCurrentPage = ref(currentPage.value)
    const handleChangeActive = (active) => {
      isActive.value = active
      if (!active) {
        tempCurrentPage.value = currentPage.value
        currentPage.value = 1
      } else currentPage.value = tempCurrentPage.value
      getData(
        props.generateSearchParams
          ? props.generateSearchParams(searchInfo.value)
          : undefined
      )
    }

    const onChangePage = async (value) => {
      currentPage.value = value
      reload()
    }

    const resetPage = () => {
      currentPage.value = 1
    }

    onMounted(() => {
      if (props.jobCode) {
        readOnly.value = validateReadOnlyLoginUser(props.jobCode)
        if (readOnly.value) {
          actions.value = master.actionDotsVerticalReadOnly
        }
      }

      if (props.tableHeaders) {
        headers.value = props.tableHeaders
      } else {
        headers.value = defaultHeader
      }

      getData(
        props.generateSearchParams
          ? props.generateSearchParams(searchInfo.value)
          : undefined
      )
      if (props.additionOnMountFunc) props.additionOnMountFunc()
    })

    return {
      getData,
      onClickOpenDuplicateDialog,
      onClickOpenUpdateDialog,
      searchInfo,
      handleSearchInput,
      openConfirmDelete,
      isShowConfirmDelete,
      onDelete,
      onClickOpenAddDialog,
      reload,
      isOpenSortDialog,
      onClickOpenSortDialog,
      actions,
      updateOrderNum,
      showConfirmUsedDialog,
      isActive,
      loading,
      handleChangeActive,
      handleRevertItem,
      currentPage,
      totalItem,
      totalPage,
      pageSize,
      items,
      onChangePage,
      showConfirmRevertDialog,
      onRevert,
      exporting,
      onClickExportExcel,
      tableData,
      curData,
      headers,
      readOnly,
      isMaster,
      resetPage
    }
  }
})

export default JMasterPage
</script>

<style scoped lang="sass">
@import '@/style/css/common.sass'
</style>
